import { AutocompleteOption } from "components/Form/components/Autocomplete/Autocomplete";
import { FormComponentPropLanguages } from "components/Form/components/FormComponentProps";
import type { SchematizedValues } from "components/Form/components/SchemaForm/SchemaForm.types";
import { requiredValidator } from "components/Form/Form.utils";
import { FormField } from "config/config";
import {
    valueAndSchemaInput,
    valueAndSchemaOutput,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { wrapScalarRootInputSchemaValue } from "components/Form/components/SchemaForm/SchemaFormInference";
import { Execution } from "graphql/generated/graphql";
import { YAMLException } from "js-yaml";
import { isEqual } from "lodash";
import { getRelativeUrl } from "utils/service";
import { parseYaml } from "utils/valueTransformers";

// A scheduler's configuration_schema describes the schedule configuration,
// not free flow inputs, so it carries its own label and docs link.
const isConfigurationSchema = (fieldName: string) =>
    fieldName === "configuration_schema";

// A SCHEMA record keeps its schema in `value`; the screen edits it with the
// same editor as a flow's input schema, under its own label and docs link.
const isSchemaRecordValue = (fieldName: string) => fieldName === "value";

const schemaFieldLabel = (fieldName: string) =>
    isConfigurationSchema(fieldName)
        ? "Configuration schema"
        : isSchemaRecordValue(fieldName)
          ? "Schema"
          : "Input schema";

const schemaFieldDocs = (fieldName: string) =>
    isConfigurationSchema(fieldName)
        ? {
              text: "Scheduling",
              urlPath: "/docs/scheduling#scheduler-configuration-schema",
          }
        : isSchemaRecordValue(fieldName)
          ? {
                text: "Schema creation and validation",
                urlPath: "/docs/schema-creation-and-validation#schema-resource",
            }
          : {
                text: "Schema creation and validation",
                urlPath: "/docs/schema-creation-and-validation#input-and-output-schema",
            };

export const valueAndSchemaInputField = (fieldName = "input_schema") => ({
    name: valueAndSchemaInput,
    component: "schemaForm", // TODO: use form with input_schema https://app.clickup.com/t/860q6x5p1
    fieldProps: {
        lang: "yaml" as FormComponentPropLanguages,
        isReadOnly: false,
        label: schemaFieldLabel(fieldName),
        docs: schemaFieldDocs(fieldName),
        columnDirection: true,
        testFormButton: true,
        canEnableSaveManually: true,
    },
    remappingSubmitDto: (value: SchematizedValues) => {
        if (value) {
            // Flow inputs are always a JSON object, so a scalar-root input_schema
            // can never run. Wrap a scalar root into an object root before
            // persisting (covers the form/enrich/code edit paths; matches the
            // backend write-gate). This is the field config that
            // submitSingleRecordForm (ResourcesSingle/utils.tsx) actually invokes
            // at submit — the JSX <Field remappingSubmitDto> is not read there.
            // Scheduler configuration_schema (fieldName !== "input_schema") is not
            // gated, so it is left unchanged. The code-view path can hand us the
            // raw YAML/JSON string, so use the string-aware variant.
            // operator-task-fe-input-schema-builder-no-scalar-root
            return [
                {
                    key: fieldName,
                    value:
                        fieldName === "input_schema"
                            ? wrapScalarRootInputSchemaValue(value.values)
                            : value.values,
                },
            ];
        } else return [];
    },
    isEqual: (valueA, valueB) => {
        return isEqual(valueA, valueB);
    },
    validation: (value) => {
        try {
            parseYaml(value.values);
            return undefined;
        } catch (error: any) {
            const typedError: YAMLException = error;
            const errorMessage = `${typedError?.name}: ${typedError?.reason}`;
            return errorMessage;
        }
    },
});

export const valueAndSchemaOutputField = {
    name: valueAndSchemaOutput,
    component: "schemaForm", // TODO: use form with input_schema https://app.clickup.com/t/860q6x5p1
    fieldProps: {
        lang: "yaml" as FormComponentPropLanguages,
        isReadOnly: false,
        label: "Output schema",
        docs: {
            text: "Schema creation and validation",
            urlPath: "/docs/schema-creation-and-validation#input-and-output-schema",
        },
        columnDirection: true,
        testFormButton: true,
        canEnableSaveManually: true,
    },
    remappingSubmitDto: (value: SchematizedValues) => {
        if (value) {
            return [
                {
                    key: "output_schema",
                    value: value.values,
                },
            ];
        } else return [];
    },
    isEqual: (valueA, valueB) => {
        return isEqual(valueA, valueB);
    },
    validation: (value) => {
        try {
            parseYaml(value.values);
            return undefined;
        } catch (error: any) {
            const typedError: YAMLException = error;
            const errorMessage = `${typedError?.name}: ${typedError?.reason}`;
            return errorMessage;
        }
    },
};

const specialExecutionLocationValue = { value: "special_value", label: "Specific project" };

export const executionLocationField = ({
    optionList,
    docs,
}: {
    optionList: AutocompleteOption[];
    docs?: { text: string; urlPath: string };
}): FormField => ({
    name: "execution_location",
    component: "extendedAutocomplete",
    fieldProps: {
        label: "Execution location",
        options: {
            list: [...optionList, specialExecutionLocationValue],
        },
        extendedAutocompleteOptions: {
            resource: "project",
        },
        specialValue: specialExecutionLocationValue,
        clearable: false,
        docs,
    },
    fieldAttr: { required: true },
    validation: requiredValidator,
});

export const latestListContainerWidget = (
    uid: string,
    getLink: (resourceId: string) => string
) => ({
    uid,
    title: "Latest executions",
    className: "w-1/3",
    component: "LatestList",
    link: getLink,
    dataTestId: "latestExecutions",
    dto: ({ stats }: any) => [
        ...(stats?.latestExecutions?.execution_list?.execution_list?.map((item: Execution) => ({
            ...item,
            link: getRelativeUrl("execution", item.id),
        })) || []),
    ],
});

export const scriptField = {
    name: "script",
    component: "visualCodeEditor",
    fieldProps: {
        lang: "python" as "python",
        isReadOnly: false,
        initialHeight: "48vh",
        breakpointsEnabled: true,
        columnDirection: true,
    },
    fieldAttr: {},
};
